import { useState, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ChevronLeft, ChevronRight, RotateCcw, Layers } from 'lucide-react';
import type { BiddingScheduleDayDto } from '../../../types/BiddingScheduleTypes';
import { BusinessDayTypeConst } from '../../../constants/BiddingScheduleConst';
import { markHolding } from '../../../utils/scheduleMark';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../../types/UserRole';

//--------------------------------
// スケジュール管理用カレンダーコンポーネント
//--------------------------------
interface Props {
  selectedDates: Dayjs[];
  onDateToggle: (date: Dayjs, isBulkMode: boolean) => void;
  onReset: () => void;
  /** 一括選択モードの解除時に呼ぶ。表示月は変えずに単独選択へ戻す（D-0065） */
  onCollapseSelection: () => void;
  viewDate: Dayjs;
  onViewDateChange: (date: Dayjs) => void;
  monthData?: Record<string, BiddingScheduleDayDto>;
  /** 表示中の出品区分（タブ）。セルの開催表記をこの区分から導く（INV-SCH-8） */
  listingCategory: number;
}

export const BiddingScheduleCalendar = ({ 
  selectedDates, 
  onDateToggle, 
  onReset,
  onCollapseSelection,
  viewDate,
  onViewDateChange,
  monthData,
  listingCategory
}: Props) => {
  // ログイン中のユーザー情報を取得
  const { currentUser } = useAuth();

  const isAdmin = currentUser?.role === UserRole.Admin;
  const isReadOnly = !isAdmin;

  //----------------
  // State定義
  //----------------
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [isBulkMode, setIsBulkMode] = useState(false);
  
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土'];
  const startOfMonth = viewDate.startOf('month');
  const daysInMonth = viewDate.daysInMonth();
  const startDayOfWeek = startOfMonth.day();

  //----------------
  // Handlers
  //----------------
  // 「今日の日付を選択」。表示月・選択日の両方を今日に戻す処理は onReset 側が持つ。
  // 🔒 ここで onViewDateChange を併せて呼ばないこと（D-0067）。
  //    ページ側では onViewDateChange と onReset の両方が未保存編集のガードに包まれているため、
  //    2つ呼ぶと表示月が今月以外のときに確認ダイアログが2回出る（run-01 SCH-B5-04）。
  const handleTodayReset = () => {
    setShowYearPicker(false);
    setShowMonthPicker(false);
    onReset();
  };

  // 一括選択モードの切替。解除時は「表示月は維持したまま」単独選択に戻す（D-0065）。
  // 以前は「今日の日付を選択」と同じ処理を共用しており、表示月まで今月に戻っていた。
  const handleToggleBulkMode = () => {
    const nextMode = !isBulkMode;
    setIsBulkMode(nextMode);
    if (!nextMode) {
      setShowYearPicker(false);
      setShowMonthPicker(false);
      onCollapseSelection();
    }
  };

  //----------------
  // Memo定義
  //----------------
  const calendarGrid = useMemo(() => {
    const grid: (Dayjs | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) grid.push(null);
    for (let d = 1; d <= daysInMonth; d++) grid.push(startOfMonth.date(d));
    return grid;
  }, [viewDate, startDayOfWeek, daysInMonth, startOfMonth]);

  // 週が6行になる月（例: 2026年8月）はカレンダーが約70px高くなり、ビューポートに収まらなくなる。
  // 6行の月だけセル高を 64px→56px に下げて吸収する（5行以下の月は 64px を維持）。
  // Tailwind は静的解析のためクラス名は literal で持つ。
  const cellHeightClass = calendarGrid.length > 35 ? 'h-14' : 'h-16';

  const years = useMemo(() => {
    const currentYear = dayjs().year();
    return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  }, []);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  //----------------
  // レンダリング
  //----------------
  return (
    <div className="w-full select-none relative px-0">
      
      {/* 1. モード切替エリア */}
      {!isReadOnly && (
      <div className="mt-1 mb-4 px-1">
        <div className="flex items-center justify-between bg-indigo-50/50 p-3 rounded-2xl border border-indigo-100 shadow-sm mb-1.5">
          <div className="flex items-center gap-3">
            <Layers className={`w-6 h-6 ${isBulkMode ? 'text-indigo-600' : 'text-slate-400'}`} />
            <span className="text-[15px] font-black text-slate-700 tracking-tighter">
              日付一括選択モード
            </span>
          </div>
          <button 
            type="button"
            onClick={handleToggleBulkMode}
            className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none ${isBulkMode ? 'bg-indigo-600' : 'bg-slate-300'}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${isBulkMode ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
        
        {/* 説明文エリア（維持） */}
        <div className="px-2 space-y-0.5">
          <p className="text-[11px] font-bold text-slate-500 leading-snug">
            ※スイッチをONにすると日付を一括選択出来ます。
          </p>
          <p className="text-[11px] font-bold text-slate-500 leading-snug">
            ※一括選択を解除すると表示中の月の単一選択に戻ります。
          </p>
        </div>
      </div>
      )}

      {/* 2. ナビゲーションヘッダー */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-3 text-2xl font-black text-slate-800 tracking-tight">
          <button type="button" onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }} className="hover:text-indigo-600 transition-colors">
            {viewDate.format('YYYY年')}
          </button>
          <button type="button" onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }} className="hover:text-indigo-600 transition-colors">
            {viewDate.format('MM月')}
          </button>
        </div>

        <div className="flex gap-1">
          <button type="button" onClick={() => onViewDateChange(viewDate.subtract(1, 'month'))} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 transition-all">
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button type="button" onClick={() => onViewDateChange(viewDate.add(1, 'month'))} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 transition-all">
            <ChevronRight className="w-8 h-8" />
          </button>
        </div>

        {(showYearPicker || showMonthPicker) && (
          <div className="absolute top-40 left-0 p-4 bg-white border border-slate-200 rounded-2xl shadow-2xl z-20 w-full">
            {showYearPicker && (
              <div className="grid grid-cols-3 gap-2">
                {years.map(y => (
                  <button key={y} type="button" onClick={() => { onViewDateChange(viewDate.year(y)); setShowYearPicker(false); }} className={`py-3 text-base font-black rounded-xl ${viewDate.year() === y ? 'bg-indigo-600 text-white' : 'hover:bg-slate-100'}`}>{y}</button>
                ))}
              </div>
            )}
            {showMonthPicker && (
              <div className="grid grid-cols-4 gap-2">
                {months.map(m => (
                  <button key={m} type="button" onClick={() => { onViewDateChange(viewDate.month(m - 1)); setShowMonthPicker(false); }} className={`py-3 text-base font-black rounded-xl ${viewDate.month() + 1 === m ? 'bg-indigo-600 text-white' : 'hover:bg-slate-100'}`}>{m}月</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. 曜日ラベル */}
      <div className="grid grid-cols-7 mb-1 text-center text-[12px] font-black">
        {daysOfWeek.map((d, i) => (
          <div key={d} className={i === 0 ? 'text-red-500' : i === 6 ? 'text-blue-500' : 'text-slate-400'}>{d}</div>
        ))}
      </div>

      {/* 4. カレンダー本体 */}
      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {calendarGrid.map((date, index) => {
          if (!date) return <div key={`empty-${index}`} className={cellHeightClass} />;
          
          const dateKey = date.format('YYYY-MM-DD');
          const isSelected = selectedDates.some(d => d.isSame(date, 'day'));
          const isToday = date.isSame(dayjs(), 'day');
          const dayData = monthData?.[dateKey];
          const isNonBusinessDay = dayData?.businessDay === BusinessDayTypeConst.NON_BUSINESS_DAY;
          const isBusinessDay = dayData?.businessDay === BusinessDayTypeConst.BUSINESS_DAY;

          // 当日開催ステータスの表記（設計 §3 の mark()・区分ごとに変わる）
          const holdingText = markHolding(listingCategory, dayData?.holdingStatus);

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => onDateToggle(date, isBulkMode)}
              className={`${cellHeightClass} relative flex flex-col items-center justify-center rounded-xl font-black transition-all border-2
                ${isSelected ? 'bg-indigo-600 text-white border-transparent shadow-md z-10 scale-[1.02]' : 
                  isNonBusinessDay ? 'bg-red-50 text-red-500 border-red-100' : 
                  'text-slate-700 bg-white border-transparent hover:border-slate-200'}
                ${isToday && !isSelected ? 'ring-2 ring-indigo-400/30 text-indigo-700' : ''}`}
            >
              <div className="absolute top-0.5 right-1 flex gap-0.5">
                {isNonBusinessDay && <span className={`text-[9px] font-bold ${isSelected ? 'text-white/60' : 'text-red-400'}`}>休</span>}
                {isBusinessDay && <span className={`text-[9px] font-bold ${isSelected ? 'text-white/60' : 'text-slate-300'}`}>営</span>}
              </div>

              <span className={`text-[18px] leading-none ${holdingText ? 'mb-0.5' : 'translate-y-0'}`}>
                {date.date()}
              </span>
              
              <div className="flex flex-col items-center justify-center w-full px-0.5">
                {isBusinessDay && holdingText && (
                  <span className={`text-[9px] font-black leading-tight whitespace-nowrap ${isSelected ? 'text-white' : 'text-indigo-500'}`}>
                    {holdingText}
                  </span>
                )}
                {/* 次回予定の表示エリアを削除しました */}
              </div>
            </button>
          );
        })}
      </div>

      {/* 5. リセットボタン */}
      <div className="flex justify-center border-t border-slate-100 pt-3 pb-1">
        <button 
          type="button"
          onClick={handleTodayReset}
          className="flex items-center gap-2.5 px-10 py-3 bg-slate-50 hover:bg-indigo-50 rounded-full text-[14px] font-black text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 transition-all active:scale-95 shadow-sm"
        >
          <RotateCcw className="w-5 h-5" />
          今日の日付を選択
        </button>
      </div>
    </div>
  );
};